import Pluralize from 'pluralize';
import React, { useCallback } from 'react';

import { PostJSON } from '../../libs/request';
import useRequestJSON from '../../libs/hooks/useRequestJSON';

import { TermResponse, Term, TermBase, Terms, TermsValue } from './types';

export type { Terms };

const TermsContext = React.createContext<TermsValue>({
  terms: null,
  isLoading: true,
  error: null,
  updateTerms: null
});

const Capitalize = (word: string): string => {
  const parts = word.split(' ');
  return parts
    .map((part) => {
      return part[0].toUpperCase() + part.slice(1);
    })
    .join(' ');
};

const GetTermsFromBase = (base: TermBase): Terms => {
  const terms: Record<string, Term> = {};
  for (const key of Object.keys(base)) {
    const value: string = base[key];
    const plural = Pluralize(value);
    terms[key] = {
      default: value,
      plural: plural,
      capitalized: Capitalize(value),
      pluralCapitalized: Capitalize(plural)
    };
  }
  return terms as Terms;
};

export const DEFAULT_TERMS = {
  patient: 'patient',
  provider: 'provider',
  mrn: 'MRN'
};

const transform = (response: TermResponse): TermBase => {
  return response.settings?.terms;
};

export const TermsProvider: React.FC = (props) => {
  const request = useCallback(
    () =>
      PostJSON<TermResponse>({
        url: '/user/data_account_settings/'
      }),
    []
  );
  const { data, setData, isLoading, error } = useRequestJSON<
    TermResponse,
    TermBase
  >({
    request,
    transformFn: transform
  });
  const value: TermsValue = {
    terms: GetTermsFromBase(data || DEFAULT_TERMS),
    isLoading,
    error,
    updateTerms: setData
  };
  return <TermsContext.Provider value={value} {...props} />;
};

export function useTerms(): TermsValue {
  const context = React.useContext(TermsContext);
  if (context === undefined) {
    throw new Error('useTerms must be used within a TermsProvider');
  }
  return context;
}
